/* In-place collection switching for the banner switch blocks
   (main-collection-banner). Progressive enhancement over the real
   links: fetch the target collection page, swap the product grid (and
   banner, when the target renders one with switches) in place, and
   pushState — no full navigation, scroll position kept.

   The target does NOT need to share the template: sections are matched
   by content (#ProductGridContainer / .collection-hero__switches), and
   when the target page has no switch row (e.g. it uses the default
   template) the current banner is kept and only the active marker
   moves. Falls back to normal navigation when the fetch fails or no
   product grid is found; modified-key clicks keep native behavior. */
(() => {
  let controller = null;

  const sectionAround = (root, selector) => {
    const el = root.querySelector(selector);
    return el ? el.closest('.shopify-section') : null;
  };

  /* keep the theme-preview `view` param (template suffix) so switching
     also works while previewing an unassigned template */
  const targetUrl = (href) => {
    const url = new URL(href, window.location.href);
    const view = new URLSearchParams(window.location.search).get('view');
    if (view && !url.searchParams.has('view')) url.searchParams.set('view', view);
    return url.toString();
  };

  const swapFrom = (doc, url) => {
    const bannerSection = sectionAround(document, '.collection-hero__switches');
    const gridSection = sectionAround(document, '#ProductGridContainer');
    const newGrid = sectionAround(doc, '#ProductGridContainer');
    if (!bannerSection || !gridSection || !newGrid) return false;

    gridSection.innerHTML = newGrid.innerHTML;

    const newBanner = sectionAround(doc, '.collection-hero__switches');
    if (newBanner) {
      bannerSection.innerHTML = newBanner.innerHTML;
    } else {
      /* target page renders no switch row — keep ours, move the marker */
      const targetPath = new URL(url, window.location.href).pathname;
      bannerSection.querySelectorAll('.collection-hero__switch').forEach((link) => {
        const isActive = new URL(link.href, window.location.href).pathname === targetPath;
        link.classList.toggle('collection-hero__switch--active', isActive);
        if (isActive) {
          link.setAttribute('aria-current', 'page');
        } else {
          link.removeAttribute('aria-current');
        }
      });
    }

    document.title = doc.title;

    /* the old drawer element is gone; make sure its body lock is too */
    document.body.classList.remove('facets-drawer-open');

    if (typeof initializeScrollAnimationTrigger === 'function') {
      initializeScrollAnimationTrigger(bannerSection);
      initializeScrollAnimationTrigger(gridSection);
    }
    /* re-apply stored client facets to the fresh grid (facets.js) */
    if (typeof herlamoraFacetsRestore === 'function') herlamoraFacetsRestore();
    return true;
  };

  const load = (url, push) => {
    if (controller) controller.abort();
    controller = new AbortController();
    const gridSection = sectionAround(document, '#ProductGridContainer');
    if (gridSection) gridSection.classList.add('collection-switching');
    return fetch(url, { signal: controller.signal })
      .then((response) => {
        if (!response.ok) throw new Error(`HTTP ${response.status}`);
        return response.text();
      })
      .then((text) => {
        const doc = new DOMParser().parseFromString(text, 'text/html');
        if (!swapFrom(doc, url)) {
          window.location.href = url;
          return;
        }
        if (push) history.pushState({ herlamoraCollectionSwitch: true }, '', url);
      })
      .catch((error) => {
        if (error.name !== 'AbortError') window.location.href = url;
      })
      .finally(() => {
        const grid = sectionAround(document, '#ProductGridContainer');
        if (grid) grid.classList.remove('collection-switching');
      });
  };

  document.addEventListener('click', (event) => {
    const link = event.target.closest('.collection-hero__switch');
    if (!link) return;
    /* keep native behavior for new-tab/-window clicks */
    if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey || event.button !== 0) return;
    event.preventDefault();
    if (link.classList.contains('collection-hero__switch--active')) return;
    load(targetUrl(link.href), true);
  });

  window.addEventListener('popstate', (event) => {
    if (event.state && event.state.herlamoraCollectionSwitch) {
      load(window.location.href, false);
      return;
    }
    /* going back from a pushed entry to the initial one lands here with
       null state — restore in place when switches are on the page */
    if (document.querySelector('.collection-hero__switches')) load(window.location.href, false);
  });
})();
